<script setup lang="ts">
import EpIcon from '~/components/icon/EpIcon.vue'
import { explorerRoutes } from '~/routes'
import { ModuleResultModel, TransactionResultModel } from '~/types/model/general/search.model'
import { searchFilterType } from '~/types/search-filter.type'

interface IProps {
  label: string
  resultList: Record<string, any>
  type: searchFilterType
}

const props = defineProps<IProps>()

const iconName = computed(() => {
  if (props.type === searchFilterType.BLOCK) {
    return 'view_in_ar'
  }
  if (props.type === searchFilterType.TRANSACTION) {
    return 'swap_horiz'
  }
  if (props.type === searchFilterType.ADDRESS) {
    return 'account_balance_wallet'
  }
  if (props.type === searchFilterType.MODULE) {
    return 'description'
  }
})

const getPath = (result: string | TransactionResultModel | ModuleResultModel) => {
  if (props.type === searchFilterType.BLOCK) {
    return explorerRoutes.getBlockDetailPage(result as string)
  }
  if (props.type === searchFilterType.TRANSACTION) {
    return explorerRoutes.getTransactionDetailPage((result as TransactionResultModel).version)
  }
  if (props.type === searchFilterType.ADDRESS) {
    return explorerRoutes.getAccountDetailPage(result as string)
  }
  if (props.type === searchFilterType.MODULE) {
    return explorerRoutes.getModulePage((result as ModuleResultModel).address, (result as ModuleResultModel).moduleName)
  }
}

const isTransaction = computed(() => {
  return props.type === searchFilterType.TRANSACTION
})

const isModule = computed(() => {
  return props.type === searchFilterType.MODULE
})
</script>

<template>
  <div v-if="resultList.length">
    <span class="label">{{ label }}</span>
    <ul>
      <li
        v-for="result in resultList"
        :key="isTransaction ? (result as TransactionResultModel).version : result as string"
      >
        <NuxtLink
          :href="getPath(result)"
          class="result-link"
          target="_blank"
        >
          <EpIcon
            v-if="iconName"
            :name="iconName"
            :size="20"
          />
          <span class="flex flex-col truncate text-sm">
            <template v-if="isTransaction">
              {{ (result as TransactionResultModel).version }}
              <span class="truncate text-xs text-default/40">{{ (result as TransactionResultModel).hash }}</span>
            </template>
            <template v-else-if="isModule">
              {{ (result as ModuleResultModel).moduleName }}
              <span class="truncate text-xs text-default/40">{{ (result as ModuleResultModel).address }}</span>
            </template>
            <template v-else><span class="truncate">{{ result }}</span></template>
          </span>
        </NuxtLink>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.label {
  @apply pl-1 text-xs font-bold text-default/40;

  @include mobile {
    @apply block px-8 pb-2;
  }
}

.result-link {
  @apply flex items-center gap-x-2 p-2;
  transition: all 0.3s ease;

  @include mobile {
    @apply px-8;
  }

  &:hover {
    @apply bg-primary/10;
  }
}
</style>